import {
  GroupedTable,
  GroupedTableGroup,
} from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { truncateLength } from "@/components/app-layout/consts";
import { ArtifactSourceDisplayName } from "@/components/display/display-name/ArtifactSourceDisplayName";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useExpertsData } from "@/data/use-experts-data";
import { useGroupExpertsData } from "@/data/use-group-experts-data";
import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import {
  useGroupCollectionsData,
  useKitCollectionsData,
} from "@/data/use-kit-collections-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useKitData } from "@/data/use-kits-data";
import { useSkillsData } from "@/data/use-skills-data";
import {
  useGroupTemplatesData,
  useKitTemplatesData,
} from "@/data/use-templates-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import type { GetKitDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faLock,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { smartDate } from "../../lib/date";
import {
  canOpenGrant,
  isOwnedArtifact,
  type GrantAccess,
} from "../../lib/grant";

type Artifact = {
  uuid: string;
  name: string;
  dateUpdated: string;
  readonly?: boolean;
  access?: GrantAccess | null;
  group: {
    uuid: string;
    name: string;
  } | null;
};

const collectionKitToArtifact = (kit: GetKitDto) => ({
  uuid: kit.uuid,
  name: kit.name,
  dateUpdated: String(kit.date_updated),
  group: kit.group ? { uuid: kit.group.uuid, name: kit.group.name } : null,
});

type GroupId = "experts" | "folders" | "templates" | "skills" | "collections";

/**
 * List all group artifacts minus the original kit artifacts.
 * The introduced `selected` property is used to show the artifact as selected.
 */
const useArtifactsWithSelected = <
  TK extends { uuid: string; readonly: boolean; access: GrantAccess | null },
  TW extends { uuid: string },
>(
  kitArtifacts: TK[],
  groupArtifacts: TW[],
) => {
  return useMemo(() => {
    const results: (TW & { selected: boolean })[] = [];
    const kitArtifactUuidMap = kitArtifacts.reduce(
      (acc, artifact) => ({ ...acc, [artifact.uuid]: artifact }),
      {} as Record<string, TK>,
    );

    for (const artifact of groupArtifacts) {
      const kitArtifact =
        artifact.uuid in kitArtifactUuidMap
          ? kitArtifactUuidMap[artifact.uuid]
          : undefined;
      if (!kitArtifact || !isOwnedArtifact(kitArtifact)) {
        results.push({
          ...artifact,
          selected: !!kitArtifact,
        });
      }
    }

    return results;
  }, [kitArtifacts, groupArtifacts]);
};

export default function ArtifactsPage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit } = useKitData(groupUuid, kitUuid);
  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Artifacts</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <p>Link or unlink artifacts from your kit.</p>
        <div className="space-y-6">
          {kit?.type === "collection" ? (
            <ArtifactPageContent canAlwaysUnlink={true} />
          ) : kit?.type === "project" ? (
            <ArtifactPageContent showCollections />
          ) : (
            <ArtifactPageContent />
          )}
        </div>
      </PageBody>
    </Page>
  );
}

function ArtifactPageContent({
  showExperts = true,
  showFolders = true,
  showSkills = true,
  showTemplates = true,
  showCollections = false,
  canAlwaysUnlink = false,
}: {
  showExperts?: boolean;
  showFolders?: boolean;
  showSkills?: boolean;
  showTemplates?: boolean;
  showCollections?: boolean;
  canAlwaysUnlink?: boolean;
}) {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const [viewingCollectionUuid, setViewingCollectionUuid] = useState<
    string | null
  >(null);

  const {
    linkFolder,
    linkExpert,
    linkTemplate,
    linkSkill,
    linkCollection,
    unlinkExpert,
    unlinkFolder,
    unlinkTemplate,
    unlinkSkill,
    unlinkCollection,
    kit,
  } = useKitData(groupUuid, kitUuid);
  const listAccess = kit?.type === "collection" ? "owner" : undefined;

  const { experts: kitExperts } = useExpertsData(groupUuid, kitUuid);
  const { skills: kitSkills } = useSkillsData(groupUuid, kitUuid);
  const { templates: kitTemplates } = useKitTemplatesData(groupUuid, kitUuid);
  const { folders: kitFolders } = useKitFoldersData(groupUuid, kitUuid);
  const { collections: kitCollectionsRaw } = useKitCollectionsData(
    groupUuid,
    kitUuid,
  );

  // Artifacts reachable only through a linked collection are managed from
  // the Collections group's own view, not this kit's direct artifact lists.
  const directKitExperts = useMemo(
    () => kitExperts.filter((expert) => !expert.linkedViaCollection),
    [kitExperts],
  );
  const directKitSkills = useMemo(
    () => kitSkills.filter((skill) => !skill.linkedViaCollection),
    [kitSkills],
  );
  const directKitTemplates = useMemo(
    () => kitTemplates.filter((template) => !template.linkedViaCollection),
    [kitTemplates],
  );
  const directKitFolders = useMemo(
    () => kitFolders.filter((folder) => !folder.linkedViaCollection),
    [kitFolders],
  );

  const { experts: groupExperts, isLoading: groupExpertsLoading } =
    useGroupExpertsData(
      groupUuid,
      listAccess ? { access: listAccess } : undefined,
    );
  const { templates: groupTemplates, isLoading: groupTemplatesLoading } =
    useGroupTemplatesData({
      groupUuid,
      access: listAccess,
    });
  const { folders: groupFolders, isLoading: groupFoldersLoading } =
    useGroupFoldersData({
      groupUuid,
      access: listAccess,
    });
  const { skills: groupSkills, isLoading: groupSkillsLoading } =
    useGroupSkillsData({
      groupUuid,
      access: listAccess,
    });
  const { collections: groupCollections, isLoading: groupCollectionsLoading } =
    useGroupCollectionsData(groupUuid);

  // A Collection is never "owned" by the kit it's linked into — a link is
  // always removable, so it is not treated as owned for the shared table.
  const kitCollections = useMemo(
    () =>
      kitCollectionsRaw.map((collection) => ({
        ...collectionKitToArtifact(collection),
        access: "read" as const,
        readonly: true,
      })),
    [kitCollectionsRaw],
  );

  const kitCollectionUuids = useMemo(
    () => new Set(kitCollections.map((collection) => collection.uuid)),
    [kitCollections],
  );

  const groupCollectionsAsArtifacts = useMemo(
    () => groupCollections.map(collectionKitToArtifact),
    [groupCollections],
  );

  const experts = useArtifactsWithSelected(kitExperts, groupExperts);
  const templates = useArtifactsWithSelected(kitTemplates, groupTemplates);
  const folders = useArtifactsWithSelected(kitFolders, groupFolders);
  const skills = useArtifactsWithSelected(kitSkills, groupSkills);
  const collections = useArtifactsWithSelected(
    kitCollections,
    groupCollectionsAsArtifacts,
  );

  const handleLinkExpert = async (expertUuid: string) => {
    try {
      await linkExpert({ expertUuid });
      toast.success("Expert linked successfully!");
    } catch {
      toast.error("Failed to link expert. Please try again.");
    }
  };

  const handleLinkCollection = async (collectionUuid: string) => {
    try {
      await linkCollection({ collectionUuid });
      toast.success("Collection linked successfully!");
    } catch {
      toast.error("Failed to link collection. Please try again.");
    }
  };

  const handleLinkFolder = async (folderUuid: string) => {
    try {
      await linkFolder({ folderUuid });
      toast.success("Folder linked successfully!");
    } catch {
      toast.error("Failed to link folder. Please try again.");
    }
  };

  const handleLinkTemplate = async (templateUuid: string) => {
    try {
      await linkTemplate({ templateUuid });
      toast.success("Template linked successfully!");
    } catch {
      toast.error("Failed to link template. Please try again.");
    }
  };

  const handleLinkSkill = async (skillUuid: string) => {
    try {
      await linkSkill({ skillUuid });
      toast.success("Skill linked successfully!");
    } catch {
      toast.error("Failed to link skill. Please try again.");
    }
  };

  const handleUnlinkArtifact = async (artifact: Artifact, type: GroupId) => {
    try {
      switch (type) {
        case "experts":
          await unlinkExpert({ expertUuid: artifact.uuid });
          toast.success("Expert unlinked successfully!");
          break;
        case "folders":
          await unlinkFolder({ folderUuid: artifact.uuid });
          toast.success("Folder unlinked successfully!");
          break;
        case "templates":
          await unlinkTemplate({ templateUuid: artifact.uuid });
          toast.success("Template unlinked successfully!");
          break;
        case "skills":
          await unlinkSkill({ skillUuid: artifact.uuid });
          toast.success("Skill unlinked successfully!");
          break;
        case "collections":
          await unlinkCollection({ collectionUuid: artifact.uuid });
          toast.success("Collection unlinked successfully!");
          break;
        default: {
          const unhandled: never = type;
          throw new Error(`Unhandled artifact type: ${unhandled}`);
        }
      }
    } catch {
      toast.error(`Failed to unlink ${type}. Please try again.`);
    }
  };

  const groupData: (GroupedTableGroup<Artifact> & { groupId: GroupId })[] = [];

  if (showExperts) {
    groupData.push({
      groupId: "experts",
      label: "Experts",
      items: directKitExperts,
      actions: (
        <AddTemplateDialog
          label="Link Expert"
          title="Workspace Experts"
          onAdd={handleLinkExpert}
          artifacts={experts}
          isLoading={groupExpertsLoading}
        />
      ),
    });
  }

  if (showFolders) {
    groupData.push({
      groupId: "folders",
      label: "Folders",
      items: directKitFolders,
      actions: (
        <AddTemplateDialog
          label="Link Folder"
          title="Workspace Folders"
          onAdd={handleLinkFolder}
          artifacts={folders}
          isLoading={groupFoldersLoading}
        />
      ),
    });
  }

  if (showTemplates) {
    groupData.push({
      groupId: "templates",
      label: "Templates",
      items: directKitTemplates,
      actions: (
        <AddTemplateDialog
          label="Link Template"
          title="Workspace Templates"
          onAdd={handleLinkTemplate}
          artifacts={templates}
          isLoading={groupTemplatesLoading}
        />
      ),
    });
  }

  if (showSkills) {
    groupData.push({
      groupId: "skills",
      label: "Skills",
      items: directKitSkills,
      actions: (
        <AddTemplateDialog
          label="Link Skill"
          title="Workspace Skills"
          onAdd={handleLinkSkill}
          artifacts={skills}
          isLoading={groupSkillsLoading}
        />
      ),
    });
  }

  if (showCollections) {
    groupData.push({
      groupId: "collections",
      label: "Collections",
      items: kitCollections,
      actions: (
        <AddTemplateDialog
          label="Link Collection"
          title="Workspace Collections"
          onAdd={handleLinkCollection}
          artifacts={collections}
          isLoading={groupCollectionsLoading}
        />
      ),
    });
  }

  return (
    <>
      <GroupedTable
        data={groupData}
        itemIdKey="uuid"
        columns={[
          { key: "name", label: "Name" },
          { key: "readonly", label: "Source" },
          { key: "dateUpdated", label: "Last Modified" },
        ]}
        renderCell={{
          name: (_, row) => {
            const label = (
              <ArtifactNameCell
                name={row.name}
                locked={!canOpenGrant(row.access)}
              />
            );

            if (!kitCollectionUuids.has(row.uuid)) {
              return label;
            }

            return (
              <button
                type="button"
                className="cursor-pointer text-left hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingCollectionUuid(row.uuid);
                }}
              >
                {label}
              </button>
            );
          },
          dateUpdated: (_, row) => smartDate(row.dateUpdated),
          readonly: (_, row) => <ArtifactSourceDisplayName artifact={row} />,
        }}
        renderActions={({ row, groupId }) => (
          <ArtifactRowActions
            row={row}
            canAlwaysUnlink={canAlwaysUnlink}
            onDelete={() => handleUnlinkArtifact(row, groupId as GroupId)}
          />
        )}
        ignoreEmptyMessage
        showSingleGroupHeader
      />
      <CollectionArtifactsDialog
        groupUuid={groupUuid}
        kitUuid={viewingCollectionUuid}
        onOpenChange={(open) => !open && setViewingCollectionUuid(null)}
      />
    </>
  );
}

function ArtifactNameCell({
  name,
  locked = false,
}: {
  name: string;
  locked?: boolean;
}) {
  const label =
    name.length > truncateLength ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{`${name.slice(0, truncateLength)}…`}</span>
          </TooltipTrigger>
          <TooltipContent>{name}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      name
    );

  if (!locked) {
    return label;
  }

  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-2"
      title="Visible in this kit, but you do not have access to open it."
    >
      <FontAwesomeIcon icon={faLock} />
      {label}
    </span>
  );
}

function CollectionArtifactsDialog({
  groupUuid,
  kitUuid,
  onOpenChange,
}: {
  groupUuid: string | null;
  kitUuid: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={!!kitUuid} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        {kitUuid && (
          <CollectionArtifactsDialogContent
            groupUuid={groupUuid}
            kitUuid={kitUuid}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CollectionArtifactsDialogContent({
  groupUuid,
  kitUuid,
}: {
  groupUuid: string | null;
  kitUuid: string;
}) {
  const { kit } = useKitData(groupUuid, kitUuid);
  const { experts: kitExperts } = useExpertsData(groupUuid, kitUuid);
  const { folders: kitFolders } = useKitFoldersData(groupUuid, kitUuid);
  const { templates: kitTemplates } = useKitTemplatesData(groupUuid, kitUuid);
  const { skills: kitSkills } = useSkillsData(groupUuid, kitUuid);

  const groupData: (GroupedTableGroup<Artifact> & { groupId: GroupId })[] = [
    { groupId: "experts", label: "Experts", items: kitExperts },
    { groupId: "folders", label: "Folders", items: kitFolders },
    { groupId: "templates", label: "Templates", items: kitTemplates },
    { groupId: "skills", label: "Skills", items: kitSkills },
  ];

  return (
    <>
      <DialogHeader>
        <DialogTitle>{kit?.name ?? "Collection"}</DialogTitle>
        {kit?.description && (
          <DialogDescription>{kit.description}</DialogDescription>
        )}
      </DialogHeader>
      <div className="max-h-[400px] overflow-y-auto">
        <GroupedTable
          data={groupData}
          itemIdKey="uuid"
          columns={[
            { key: "name", label: "Name" },
            { key: "readonly", label: "Source" },
            { key: "dateUpdated", label: "Last Modified" },
          ]}
          renderCell={{
            name: (_, row) => (
              <ArtifactNameCell
                name={row.name}
                locked={!canOpenGrant(row.access)}
              />
            ),
            dateUpdated: (_, row) => smartDate(row.dateUpdated),
            readonly: (_, row) => <ArtifactSourceDisplayName artifact={row} />,
          }}
          ignoreEmptyMessage
          showSingleGroupHeader
        />
      </div>
    </>
  );
}

function AddTemplateDialog({
  label,
  title,
  onAdd,
  artifacts,
  isLoading,
}: {
  label: string;
  title: string;
  onAdd: (artifactUuid: string) => void;
  artifacts: (Artifact & { selected: boolean })[];
  isLoading: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredArtifacts = useMemo(
    () =>
      artifacts.filter((artifact) => {
        return artifact.name.toLowerCase().includes(search.toLowerCase());
      }),
    [artifacts, search],
  );

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          onClick={() => setIsDialogOpen(true)}
          disabled={isLoading}
          size="sm"
        >
          <FontAwesomeIcon icon={faLink} /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-fit max-w-4xl min-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredArtifacts.map((artifact) => (
                <TableRow key={artifact.uuid}>
                  <TableCell>
                    {artifact.name.length > truncateLength ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{`${artifact.name.slice(0, truncateLength)}…`}</span>
                          </TooltipTrigger>
                          <TooltipContent>{artifact.name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      artifact.name
                    )}
                  </TableCell>
                  <TableCell>
                    <ArtifactSourceDisplayName artifact={artifact} />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => onAdd(artifact.uuid)}
                      disabled={isLoading || artifact.selected}
                      size="sm"
                    >
                      <FontAwesomeIcon icon={faLink} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArtifactRowActions({
  row,
  canAlwaysUnlink = false,
  onDelete,
}: {
  row: {
    uuid: string;
    name: string;
    readonly?: boolean;
    access?: GrantAccess | null;
  };
  canAlwaysUnlink?: boolean;
  onDelete: (kitUuid: string) => void;
}) {
  const canUnlink = !isOwnedArtifact(row) || canAlwaysUnlink;

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          disabled={!canUnlink}
          title={
            !canUnlink
              ? "Can't unlink original kit artifacts."
              : "Unlink artifact from kit."
          }
        >
          <FontAwesomeIcon icon={faUnlink} /> Unlink
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unlink {row.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently unlink this
            artifact from your kit and might not be possible to recover.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              onDelete(row.uuid);
            }}
            asChild
          >
            <Button size="sm">Unlink</Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
